import type { CSS } from '@meterup/atto';
import {
  Button,
  colors,
  ControlGroup,
  darkThemeSelector,
  selectors,
  shadows,
  sizing,
  styled,
  zIndexes,
} from '@meterup/atto';

const flexiColumnsZIndexes = {
  background: zIndexes.base + 1,
  foreground: zIndexes.base + 2,
  peek: zIndexes.base + 3,
  divider: zIndexes.base + 4,
};

export const FLEXI_COLUMNS_ANIMATION_MS = 200;

export const FLEXI_COLUMNS_RESIZE_BOUNDS = {
  min: 360,
  max: 720,
};

const flexiColumnsToggleButtonStyles = {
  minWidth: 0,
  backgroundColor: colors.bgApplicationLight,
  boxShadow: shadows.overlayLight,

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },
};

export const FlexiColumnsToggleButton = styled(Button, {
  position: 'absolute',

  '@notDesktop': {
    top: '$10',
  },

  '@desktop': {
    top: '$10',
    transform: 'translateX(-50%)',
  },

  variants: {
    position: {
      start: {
        '@notDesktop': {
          left: '-$4',
          borderRadius: '0 $8 $8 0',
        },

        '@desktop': {
          left: `$${sizing.primary}`,
          transform: 'translateX(-50%)',
        },
      },
      end: {
        '@notDesktop': {
          right: '-$4',
          borderRadius: '$8 0 0 $8',
        },

        '@desktop': {
          right: `$${sizing.primary}`,
          transform: 'translateX(50%)',
        },
      },
    },
  },

  compoundVariants: [
    {
      position: 'start',
      css: { ...flexiColumnsToggleButtonStyles },
    },
    {
      position: 'end',
      css: { ...flexiColumnsToggleButtonStyles },
    },
  ],
});
FlexiColumnsToggleButton.displayName = 'FlexiColumnsToggleButton';

export const FlexiColumnsColumn = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'auto',
  background: colors.bgApplicationLight,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
  },

  '@notDesktop': {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    transition: `transform ${FLEXI_COLUMNS_ANIMATION_MS}ms ease`,
    willChange: 'transform',
  },

  variants: {
    hasDivider: {
      true: {},
      false: {},
    },
    position: {
      start: {
        '@desktop': {
          gridArea: 'start',
          strokeRight: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            strokeRight: colors.strokeNeutralDark,
          },
        },

        '@notDesktop': {
          zIndex: flexiColumnsZIndexes.foreground,
        },
      },
      end: {
        '@desktop': {
          gridArea: 'end',
          strokeLeft: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            strokeLeft: colors.strokeNeutralDark,
          },
        },

        '@notDesktop': {
          zIndex: flexiColumnsZIndexes.background,
        },
      },
    },
    visible: {
      true: {
        zIndex: 2,

        '@notDesktop': {
          overflow: 'auto',
          transform: 'translateX(0)',
        },

        '@desktop': {
          minWidth: 0,
          overflow: 'auto',
        },
      },
      false: {
        zIndex: flexiColumnsZIndexes.peek,

        '@notDesktop': {
          overflow: 'visible',
          boxShadow: shadows.overlayLight,
          transform: `translateX(calc(-100% + ${sizing.primary}px))`,

          [darkThemeSelector]: {
            boxShadow: shadows.overlayDark,
          },

          [selectors.focus]: {
            outline: 'none',
          },
        },

        '@desktop': {
          overflow: 'hidden',
          minWidth: 0,
        },
      },
    },
  },

  compoundVariants: [
    {
      position: 'start',
      visible: false,
      css: {
        '@notDesktop': {
          transform: 'translateX(-100%)',
        },
        '@desktop': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        },
      },
    },
    {
      position: 'end',
      visible: false,
      css: {
        '@notDesktop': {
          transform: 'translateX(100%)',
        },
      },
    },
    {
      hasDivider: true,
      position: 'start',
      css: {
        '@desktop': {
          strokeRight: colors.transparent,

          [darkThemeSelector]: {
            strokeRight: colors.transparent,
          },
        },
      },
    },
    {
      position: 'end',
      visible: false,
      css: {
        '@desktop': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        },
      },
    },
    {
      hasDivider: true,
      position: 'end',
      css: {
        '@desktop': {
          strokeLeft: colors.transparent,

          [darkThemeSelector]: {
            strokeLeft: colors.transparent,
          },
        },
      },
    },
  ],
});
FlexiColumnsColumn.displayName = 'FlexiColumnsColumn';

export const FlexiColumnsContainer = styled('div', {
  position: 'relative',
  width: '100%',
  height: '100%',
  minHeight: '0',

  '@notDesktop': {
    overflowX: 'hidden',
  },

  '@desktop': {
    display: 'grid',
    gridTemplateRows: '1fr',
  },
});
FlexiColumnsContainer.displayName = 'FlexiColumnsContainer';

export const FlexiColumnsOverlay = styled('div', {
  position: 'absolute',
  inset: 0,
  zIndex: flexiColumnsZIndexes.peek,
  pointerEvents: 'none',

  '& > *': {
    pointerEvents: 'auto',
  },
});
FlexiColumnsOverlay.displayName = 'FlexiColumnsOverlay';

export const FlexiColumnsControlsContainer = styled(ControlGroup, {
  position: 'absolute',
  top: '$10',
  left: 0,
  transform: 'translateX(-50%)',
  backgroundColor: colors.bgApplicationLight,
  boxShadow: shadows.overlayLight,
  borderRadius: '$6',

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },
});
FlexiColumnsControlsContainer.displayName = 'FlexiColumnsControlsContainer';

const DIVIDER_WIDTH = '$1';

export const FlexiColumnsDivider = styled('div', {
  gridArea: 'divider',
  position: 'relative',
  widthAll: DIVIDER_WIDTH,
  zIndex: flexiColumnsZIndexes.divider,
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    backgroundColor: colors.strokeNeutralDark,
  },
});
FlexiColumnsDivider.displayName = 'FlexiColumnsDivider';

export const FlexiColumnsPeek = styled('div', {
  width: 'max-content',
  minWidth: '100%',
});
FlexiColumnsPeek.displayName = 'FlexiColumnsPeek';

export function getFlexiColumnsGridTemplateColumns(
  preset: 'narrow-wide' | 'wide-narrow' | 'even-split',
  startWidth?: number,
  endWidth?: number,
): string {
  const narrowWidth = '$400';
  const defaultStartWidth = preset === 'narrow-wide' ? narrowWidth : '1fr';
  const defaultEndWidth = preset === 'wide-narrow' ? narrowWidth : '1fr';

  const start = startWidth != null ? `$${startWidth}` : defaultStartWidth;
  const end = endWidth != null ? `$${endWidth}` : defaultEndWidth;

  return `${start} ${end}`;
}

export function getFlexiColumnsContainerCSS(
  preset: 'narrow-wide' | 'wide-narrow' | 'even-split',
  isMobile: boolean,
  startWidth?: number,
  endWidth?: number,
  startVisible?: boolean,
  endVisible?: boolean,
  /** When both panes visible on desktop, use resizable layout with divider. */
  hasDivider?: boolean,
): CSS {
  if (isMobile) {
    return {
      gridTemplateColumns: '1fr',
      gridTemplateAreas: '"start"',
    } as CSS;
  }
  // Desktop: when one pane is collapsed, give it a peek width (like DualPanes) so the
  // stroke and toggle button remain visible. No divider in collapsed state.
  const bothVisible = startVisible !== false && endVisible !== false;
  if (!bothVisible) {
    const peekWidth = `$${sizing.primary}`;
    return {
      gridTemplateAreas: '"start end"',
      gridTemplateColumns: startVisible ? `1fr ${peekWidth}` : `${peekWidth} 1fr`,
    } as CSS;
  }
  if (hasDivider && startWidth != null) {
    return {
      gridTemplateAreas: '"start divider end"',
      gridTemplateColumns: `${startWidth}px ${DIVIDER_WIDTH} 1fr`,
    } as CSS;
  }
  return {
    gridTemplateAreas: '"start end"',
    gridTemplateColumns: getFlexiColumnsGridTemplateColumns(preset, startWidth, endWidth),
  } as CSS;
}
